import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ConversationAttributeItem, withSecondsSuffix } from '@/entities/attributes';
import { useAllConversationsAttributes } from '@/entities/conversations';
import { useTimeDisplayFormat } from '@/entities/settings';
import { getAttributeValueKind, isManualValueAttribute } from '@/features/attribute-filters';
import type { AddOption } from '@/shared/ui';
import {
  CALL_TIME_PROPERTY,
  CLIENT_PROPERTY,
  EMPLOYEE_PROPERTY,
  type RecordPropertyEntry,
  toAttributeProperty,
} from './recordValueSet';

/** Удалённые атрибуты в набор значений не предлагаются; ссылка постоянная — это ключ запроса */
const ATTRIBUTES_PARAMS = { 'is-deleted': false } as const;

/**
 * Свойства записи для набора значений: сотрудник, клиент, время звонка и атрибуты, которым значение
 * задаётся вручную. Отдаёт ещё не добавленные свойства, подписи всех свойств и атрибуты набора
 * по их путям.
 */
export const useRecordProperties = (entries: RecordPropertyEntry[]) => {
  const { t } = useTranslation(['records', 'common']);
  const timeDisplayFormat = useTimeDisplayFormat();
  const { data } = useAllConversationsAttributes(ATTRIBUTES_PARAMS);

  const attributes = useMemo(
    () =>
      (data?.attributes ?? []).filter(
        (attribute) =>
          isManualValueAttribute(attribute) && getAttributeValueKind(attribute, timeDisplayFormat) !== null,
      ),
    [data?.attributes, timeDisplayFormat],
  );

  const attributesByPath = useMemo(
    () => new Map<string, ConversationAttributeItem>(attributes.map((attribute) => [attribute.path, attribute])),
    [attributes],
  );

  const allOptions = useMemo<AddOption[]>(
    () => [
      { value: EMPLOYEE_PROPERTY, label: t('records:valueSet.employee') },
      { value: CLIENT_PROPERTY, label: t('records:valueSet.client') },
      { value: CALL_TIME_PROPERTY, label: t('records:valueSet.callTime') },
      ...attributes.map((attribute) => ({
        value: toAttributeProperty(attribute.path),
        label: withSecondsSuffix(
          attribute.pathDescription || attribute.name || attribute.path,
          attribute,
          timeDisplayFormat,
          t('common:secondsSuffix'),
        ),
      })),
    ],
    [attributes, timeDisplayFormat, t],
  );

  const propertyLabels = useMemo(
    () => new Map<string, string>(allOptions.map((option) => [option.value, option.label])),
    [allOptions],
  );

  const options = useMemo(() => {
    const addedProperties = new Set(entries.map((entry) => entry.property));
    return allOptions.filter((option) => !addedProperties.has(option.value));
  }, [allOptions, entries]);

  return { options, propertyLabels, attributesByPath, timeDisplayFormat };
};
